import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { catchError, debounceTime } from 'rxjs/operators';
import { Subscription, throwError } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  AdministrativeConsolidatedSchedulesReportService,
  ConsolidatedScheduleHistory,
  ConsolidatedScheduleRow,
  ConsolidatedSchedulesFilter,
  ConsolidatedSchedulesResponse,
  PaidConsolidatedSchedulesFilter,
  PaidNFSeStatusFilter,
} from 'src/app/services/administrative-consolidated-schedules-report.service';
import { LoginService } from 'src/app/services/login.service';
import { TransferConfirmationModalComponent } from './modals/transfer-confirmation/transfer-confirmation.modal';
import { AdministrativeConsolidatedSchedulesTransferService } from 'src/app/services/administrative-consolidated-schedules-transfer.service';
import { InaptTransferModalComponent } from './modals/inapt-transfer/inapt-transfer.modal';
import {
  PaidConsolidatedSchedulesFiltersModalComponent,
  PaidAdvancedFilters,
} from './modals/paid-filters/paid-filters.modal';
import {
  AvailableConsolidatedSchedulesFiltersModalComponent,
  AvailableAdvancedFilters,
} from './modals/available-filters/available-filters.modal';
import { error } from 'console';

const MAX_PAGE_SIZE = 500;
const MIN_LOADING_SPINNER = 200;
const MIN_SELECTION_SPINNER = 80;

@Component({
  selector: 'app-administrative-consolidated-schedules-report',
  templateUrl: './consolidated-schedules-report.component.html',
  styleUrls: ['./consolidated-schedules-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdministrativeConsolidatedSchedulesReportComponent implements OnInit, OnDestroy {
  readonly PROFESSIONAL_MAX_CHARS = 20;
  readonly PATIENT_MAX_CHARS = 20;
  readonly SPECIALTY_MAX_CHARS = 15;
  readonly COMPLEMENTARY_SERVICES_MAX_CHARS = 45;
  readonly REASON_MAX_CHARS = 50;
  readonly PAID_REASON_MAX_CHARS = 60;
  readonly tabs: Array<'aptos' | 'inaptos' | 'pagos'> = ['aptos', 'inaptos', 'pagos'];
  activeTab: 'aptos' | 'inaptos' | 'pagos' = 'aptos';
  loading = false;
  selectionWorking = false;
  actionLoadingId: string | null = null;
  selectionRowLoadingId: string | null = null;
  selectionAllLoading = false;

  dataSource = new MatTableDataSource<ConsolidatedScheduleRow>([]);
  readonly baseColumns: string[] = [
    'indicationId',
    'appointmentDate',
    'doctorName',
    'patientName',
    'hasDocument',
    'specialty',
    'amount',
    'complementaryServices',
    'serviceDoctorAmount',
    'totalDoctorToPay',
    'planName',
    'actions',
  ];
  readonly paidColumns: string[] = [
    'indicationId',
    'appointmentDate',
    'doctorName',
    'patientName',
    'specialty',
    'planName',
    'hasDocument',
    'amount',
    'complementaryServices',
    'serviceDoctorAmount',
    'totalDoctorToPay',
    'transferId',
    'transferStatus',
    'transferReceipt',
    'nfseIssued',
    'nfseStatus',
    'nfseUrl',
    'manualPaymentReason',
    'method',
    'paymentDate',
    'actionsPayment',
  ];
  displayedColumns: string[] = [];

  total = 0;
  pageIndex = 0;
  pageSize = 10;
  pageSizeOptions: number[] = [10, 25, 50, 100, 250, 500];
  useMaxPage = true;

  filters: FormGroup<{ consolidatedHistoryId: FormControl<string | null> }>;
  historyInputControl = new FormControl<string>('', { nonNullable: true });
  availableHistories: ConsolidatedScheduleHistory[] = [];
  filteredHistories: ConsolidatedScheduleHistory[] = [];
  selectedHistory: ConsolidatedScheduleHistory | null = null;
  showHistoryList = false;
  selectionEnabled = true;
  selection = new SelectionModel<ConsolidatedScheduleRow>(true, []);
  paidAdvancedFilters: PaidAdvancedFilters | null = null;
  paidFiltersApplied = false;
  paidFiltersSummary = '';
  availableAdvancedFilters: AvailableAdvancedFilters | null = null;
  availableFiltersApplied = false;
  availableFiltersSummary = '';
  readonly nfseStatusLabels: Record<PaidNFSeStatusFilter, string> = {
    emitted: 'Emitida',
    not_emitted: 'Não emitida',
    error: 'Erro',
  };

  private subs = new Subscription();
  private loadingStartedAt = 0;
  private loadingTimer: any = null;
  private selectionBusyStartedAt = 0;
  private selectionBusyTimer: any = null;

  constructor(
    private readonly service: AdministrativeConsolidatedSchedulesReportService,
    private readonly fb: FormBuilder,
    private readonly loginService: LoginService,
    private readonly dialog: MatDialog,
    private readonly transferService: AdministrativeConsolidatedSchedulesTransferService,
    private readonly cd: ChangeDetectorRef
  ) {
    this.filters = this.fb.group({
      consolidatedHistoryId: this.fb.control<string | null>(null),
    });
    this.updateDisplayedColumns();
  }

  ngOnInit(): void {
    this.subs.add(
      this.historyInputControl.valueChanges.pipe(debounceTime(150)).subscribe(() => {
        this.applyHistoryFilter();
        this.showHistoryList = true;
      })
    );

    this.applySearch();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  applySearch(event?: PageEvent): void {
    requestAnimationFrame(() => {
      this.onSearch(event);
    });
  }

  onSearch(event?: PageEvent): void {
    this.startLoading();

    if (this.activeTab === 'aptos') {
      this.searchAptas(event);
      return;
    }

    if (this.activeTab === 'inaptos') {
      this.searchInaptas(event);
      return;
    }

    this.searchPagos(event);
  }

  private searchAptas(event?: PageEvent): void {
    this.useMaxPage = false;

    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
    } else {
      this.pageIndex = 0;
    }

    const perPage = Math.min(Math.max(this.pageSize, 1), MAX_PAGE_SIZE);
    this.pageSize = perPage;

    this.selection.clear();

    const filter = this.buildAvailableFilter();
    const historyId = filter.consolidatedHistoryId ?? null;

    this.service
      .searchAvailable({
        filter,
        page: this.pageIndex + 1,
        perPage,
      })
      .subscribe(
        (response) => this.handleSearchSuccess(response, historyId),
        () => this.handleSearchError('Erro ao carregar pagamentos aptos.')
      );
  }

  private searchInaptas(event?: PageEvent): void {
    this.useMaxPage = false;

    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
    } else {
      this.pageIndex = 0;
    }

    const perPage = Math.min(Math.max(this.pageSize, 1), MAX_PAGE_SIZE);
    this.pageSize = perPage;

    const filter = this.buildFilter();
    const historyId = filter.consolidatedHistoryId ?? null;

    this.service
      .searchUnavailable({
        filter,
        page: this.pageIndex + 1,
        perPage,
      })
      .subscribe(
        (response) => this.handleSearchSuccess(response, historyId),
        () => this.handleSearchError('Erro ao carregar pagamentos pendentes.')
      );
  }

  private searchPagos(event?: PageEvent): void {
    this.useMaxPage = false;

    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
    } else {
      this.pageIndex = 0;
    }

    const perPage = Math.min(Math.max(this.pageSize, 1), MAX_PAGE_SIZE);
    this.pageSize = perPage;

    const filter = this.buildPaidFilter();
    const historyId = filter.consolidatedHistoryId ?? null;

    this.service
      .searchPaid({
        filter,
        page: this.pageIndex + 1,
        perPage,
      })
      .subscribe(
        (response) => this.handleSearchSuccess(response, historyId),
        () => this.handleSearchError('Erro ao carregar pagamentos realizados.')
      );
  }

  private handleSearchSuccess(
    response: ConsolidatedSchedulesResponse,
    historyId: string | null
  ): void {
    const rows = response.rows ?? [];
    this.availableHistories = this.sortHistories(response.availableHistories ?? []);

    const resolvedHistory = this.resolveSelectedHistory(
      historyId,
      response.selectedHistory ?? null,
      this.availableHistories
    );

    this.selectedHistory = resolvedHistory;
    if (resolvedHistory?.id && resolvedHistory.id !== historyId) {
      this.filters.patchValue({ consolidatedHistoryId: resolvedHistory.id }, { emitEvent: false });
    }

    this.syncHistoryInputDisplay();

    this.total = response.total ?? rows.length ?? 0;
    if (this.useMaxPage) {
      this.pageSize = this.total || this.pageSize;
      this.pageIndex = 0;
    }
    this.syncPageSizeOptions(this.total);
    this.renderRowsInChunks(rows ?? []);
  }



  private handleSearchError(message: string): void {
    this.dataSource.data = [];
    this.total = 0;
    this.selectedHistory = null;
    this.selection.clear();
    this.clearLoadingTimer();
    this.updateDisplayedColumns();
    this.syncHistoryInputDisplay();
    this.loginService.showMessage(message, 3000);
    this.finishLoading();
  }

  private buildFilter(): ConsolidatedSchedulesFilter {
    const filter: ConsolidatedSchedulesFilter = {};
    const historyId = this.filters.value.consolidatedHistoryId;
    if (historyId) {
      filter.consolidatedHistoryId = historyId;
    }

    return filter;
  }

  private buildAvailableFilter(): ConsolidatedSchedulesFilter {
    const base = this.buildFilter();
    const filter: ConsolidatedSchedulesFilter = { ...base };
    const advanced = this.availableAdvancedFilters;

    const doctorName = advanced?.doctorName?.trim();
    if (doctorName) {
      filter.doctorName = doctorName;
    }

    return filter;
  }

  private buildPaidFilter(): PaidConsolidatedSchedulesFilter {
    const base = this.buildFilter();
    const filter: PaidConsolidatedSchedulesFilter = { ...base };
    const advanced = this.paidAdvancedFilters;

    const doctorName = advanced?.doctorName?.trim();
    if (doctorName) {
      filter.doctorName = doctorName;
    }

    if (advanced?.nfseStatus) {
      filter.nfseStatus = advanced.nfseStatus;
    }

    return filter;
  }

  applyHistoryFilter(): void {
    const term = this.historyInputControl.value?.trim().toLowerCase() ?? '';

    if (!term) {
      this.filteredHistories = [...this.availableHistories];
      return;
    }

    this.filteredHistories = this.availableHistories.filter((history) =>
      this.buildHistoryLabel(history).toLowerCase().includes(term)
    );
  }

  buildHistoryLabel(history: ConsolidatedScheduleHistory): string {
    const start = this.formatDate(history.startDate);
    const end = this.formatDate(history.endDate);
    return `Semana de ${start} a ${end}`;
  }

  openHistoryList(): void {
    this.showHistoryList = true;
  }

  closeHistoryList(): void {
    setTimeout(() => {
      this.showHistoryList = false;
      this.syncHistoryInputDisplay();
    }, 150);
  }

  get isPaidTab(): boolean {
    return this.activeTab === 'pagos';
  }

  get isAptosTab(): boolean {
    return this.activeTab === 'aptos';
  }

  get activeFiltersApplied(): boolean {
    if (this.isPaidTab) return this.paidFiltersApplied;
    if (this.isAptosTab) return this.availableFiltersApplied;
    return false;
  }

  onFiltersClick(): void {
    if (this.isPaidTab) {
      this.onPaidFiltersClick();
    } else if (this.isAptosTab) {
      this.onAvailableFiltersClick();
    }
  }

  onPaidFiltersClick(): void {
    if (!this.isPaidTab) return;
    if (this.paidFiltersApplied) {
      this.clearPaidFilters();
      return;
    }

    this.openPaidFiltersModal();
  }

  private openPaidFiltersModal(): void {
    const ref = this.dialog.open(PaidConsolidatedSchedulesFiltersModalComponent, {
      width: '550px',
      data: { initial: this.paidAdvancedFilters },
      disableClose: true,
    });

    this.subs.add(
      ref.afterClosed().subscribe((result: PaidAdvancedFilters | 'close' | undefined) => {
        if (!result || result === 'close') return;

        const hasAny = !!result.doctorId || !!result.nfseStatus;
        this.paidAdvancedFilters = hasAny ? result : null;
        this.paidFiltersApplied = hasAny;
        this.updatePaidFiltersSummary();
        if (this.isPaidTab) {
          this.applySearch();
        }
      })
    );
  }

  private clearPaidFilters(): void {
    this.paidAdvancedFilters = null;
    this.paidFiltersApplied = false;
    this.paidFiltersSummary = '';
    if (this.isPaidTab) {
      this.applySearch();
    }
  }

  onAvailableFiltersClick(): void {
    if (!this.isAptosTab) return;
    if (this.availableFiltersApplied) {
      this.clearAvailableFilters();
      return;
    }

    this.openAvailableFiltersModal();
  }

  private openAvailableFiltersModal(): void {
    const ref = this.dialog.open(AvailableConsolidatedSchedulesFiltersModalComponent, {
      width: '400px',
      data: { initial: this.availableAdvancedFilters },
      disableClose: true,
    });

    this.subs.add(
      ref.afterClosed().subscribe((result: AvailableAdvancedFilters | 'close' | undefined) => {
        if (!result || result === 'close') return;

        const hasAny = !!result.doctorId;
        this.availableAdvancedFilters = hasAny ? result : null;
        this.availableFiltersApplied = hasAny;
        this.updateAvailableFiltersSummary();
        if (this.isAptosTab) {
          this.applySearch();
        }
      })
    );
  }

  private clearAvailableFilters(): void {
    this.availableAdvancedFilters = null;
    this.availableFiltersApplied = false;
    this.availableFiltersSummary = '';
    if (this.isAptosTab) {
      this.applySearch();
    }
  }

  private updatePaidFiltersSummary(): void {
    if (!this.paidAdvancedFilters) {
      this.paidFiltersSummary = '';
      return;
    }

    const parts: string[] = [];
    const doctorId = this.paidAdvancedFilters.doctorId;
    const doctorName = this.paidAdvancedFilters.doctorName?.trim();
    if (doctorName || doctorId) {
      const doctorLabel = doctorName || 'Médico selecionado';
      parts.push(`Médico: ${doctorLabel}`);
    }

    if (this.paidAdvancedFilters.nfseStatus) {
      const label =
        this.nfseStatusLabels[this.paidAdvancedFilters.nfseStatus] ??
        this.paidAdvancedFilters.nfseStatus;
      parts.push(`NFSe: ${label}`);
    }

    this.paidFiltersSummary = parts.length ? parts.join(' | ') : 'Filtros avançados ativos';
  }

  private updateAvailableFiltersSummary(): void {
    if (!this.availableAdvancedFilters) {
      this.availableFiltersSummary = '';
      return;
    }

    const doctorName = this.availableAdvancedFilters.doctorName?.trim();
    if (doctorName) {
      this.availableFiltersSummary = `Médico: ${doctorName}`;
    } else {
      this.availableFiltersSummary = '';
    }
  }

  selectHistory(history: ConsolidatedScheduleHistory): void {
    this.filters.patchValue({ consolidatedHistoryId: history.id }, { emitEvent: false });
    this.selectedHistory = history;
    this.historyInputControl.setValue(this.buildHistoryLabel(history), {
      emitEvent: false,
    });
    this.onSearch();
    this.showHistoryList = false;
  }

  syncHistoryInputDisplay(): void {
    const label = this.selectedHistory ? this.buildHistoryLabel(this.selectedHistory) : '';

    this.historyInputControl.setValue(label, { emitEvent: false });
    this.filteredHistories = [...this.availableHistories];
  }

  formatDate(value: string | number | Date | null | undefined, withTime = false): string {
    if (!value) return '-';

    const dateValue =
      value instanceof Date
        ? value.getTime()
        : typeof value === 'number'
          ? value
          : Date.parse(String(value));
    if (Number.isNaN(dateValue)) return '-';

    const date = new Date(dateValue);
    const options: Intl.DateTimeFormatOptions = withTime
      ? {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }
      : {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        };

    return new Intl.DateTimeFormat('pt-BR', options).format(date);
  }

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(Number(value));
  }

  formatHasDocument(hasDocument: boolean | null | undefined): string {
    if (hasDocument === null || hasDocument === undefined) return '-';
    return hasDocument ? 'Sim' : 'Não';
  }

  formatTransferStatus(status: string | null | undefined): string {
    const value = this.sanitizeText(status);
    if (!value) return '-';

    const map: Record<string, string> = {
      DONE: 'Realizada',
      PENDING: 'Aguardando',
      FAILED: 'Falhou',
      ERROR: 'Erro',
      PROCESSING: 'Em processamento',
      CREATED: 'Criada',
    };

    const upper = value.toUpperCase();
    return map[upper] ?? value;
  }

  allowEmitNFSe(status?: string) {
    return !status || status === 'CANCELED';
  }

  allowSyncNFSe(status?: string) {
    return !!status;
  }

  scheduleNFSe(transferId: string) {
    this.transferService
      .scheduleNFSe(transferId)
      .pipe(
        catchError((error) => {
          this.loginService.showMessage('Não foi possível agendar a nota fiscal');
          return throwError(() => error);
        })
      )
      .subscribe(({ success }) => {
        if (!success) {
          this.loginService.showMessage('Não foi possível agendar a nota fiscal');
        } else {
          this.onSearch();
        }
      });
  }

  syncNFSe(transferId: string) {
    this.transferService
      .syncInvoice(transferId)
      .pipe(
        catchError(({ error }) => {
          this.loginService.showMessage(error.message);
          return throwError(() => error);
        })
      )
      .subscribe(() => {
        this.onSearch();
      });
  }

  formatNFSeStatus(status: string | null | undefined): string {
    const value = this.sanitizeText(status);
    if (!value) return '-';

    const map: Record<string, string> = {
      SCHEDULED: 'Agendada',
      AUTHORIZED: 'Autorizada',
      PROCESSING_CANCELLATION: 'Cancelamento em processamento',
      CANCELED: 'Cancelada',
      CANCELLATION_DENIED: 'Cancelamento negado',
      ERROR: 'Erro',
    };

    const upper = value.toUpperCase();
    return map[upper] ?? value;
  }

  formatMethod(method: string | null | undefined): string {
    const value = this.sanitizeText(method);
    if (!value) return '-';
    if (value.toUpperCase() === 'AUTOMATIC') return 'Automático';
    if (value.toUpperCase() === 'MANUAL') return 'Manual';
    return value;
  }

  formatBoolean(value: boolean | null | undefined): string {
    if (value === null || value === undefined) return '-';
    return value ? 'Sim' : 'Não';
  }

  getManualReasonDisplay(row: ConsolidatedScheduleRow): string {
    return this.truncateText(row.manualPaymentReason, this.PAID_REASON_MAX_CHARS);
  }

  getManualReasonTooltip(row: ConsolidatedScheduleRow): string | null {
    return this.getTooltipValue(row.manualPaymentReason, this.PAID_REASON_MAX_CHARS);
  }

  getReasonOrStatusValue(row: ConsolidatedScheduleRow): string | null {
    const reason = this.sanitizeText(row.paymentFailReason);
    if (reason) return reason;

    const status = row.transferStatus ? row.transferStatus : null;
    return this.sanitizeText(status);
  }

  getReasonDisplay(row: ConsolidatedScheduleRow): string {
    const value = this.getReasonOrStatusValue(row);
    return value ? this.truncateText(value, this.REASON_MAX_CHARS) : '-';
  }

  getReasonTooltip(row: ConsolidatedScheduleRow): string | null {
    const value = this.getReasonOrStatusValue(row);
    if (!value) return null;
    return this.needsTooltip(value, this.REASON_MAX_CHARS) ? value : null;
  }

  truncateText(value: string | null | undefined, maxLength: number): string {
    const sanitized = this.sanitizeText(value);
    if (!sanitized) return '-';
    if (sanitized.length <= maxLength) return sanitized;
    return `${sanitized.slice(0, maxLength).trimEnd()}...`;
  }

  needsTooltip(value: string | null | undefined, maxLength: number): boolean {
    const sanitized = this.sanitizeText(value);
    return !!sanitized && sanitized.length > maxLength;
  }

  getTooltipValue(value: string | null | undefined, maxLength: number): string | null {
    return this.needsTooltip(value, maxLength) ? this.sanitizeText(value) : null;
  }

  private sanitizeText(value: string | null | undefined): string | null {
    if (value === null || value === undefined) return null;
    const trimmed = value.toString().trim();
    return trimmed.length ? trimmed : null;
  }

  getTransferAmount(row: ConsolidatedScheduleRow): string {
    return this.formatCurrency(row.amount ?? null);
  }

  getComplementaryServicesDisplay(row: ConsolidatedScheduleRow): string {
    return this.truncateText(
      row.complementaryServices,
      this.COMPLEMENTARY_SERVICES_MAX_CHARS
    );
  }

  getComplementaryServicesTooltip(row: ConsolidatedScheduleRow): string | null {
    return this.getTooltipValue(
      row.complementaryServices,
      this.COMPLEMENTARY_SERVICES_MAX_CHARS
    );
  }

  getServiceAmount(row: ConsolidatedScheduleRow): string {
    return this.formatCurrency(this.getServiceAmountValue(row));
  }

  getTotalToPay(row: ConsolidatedScheduleRow): string {
    return this.formatCurrency(this.getTotalToPayValue(row));
  }

  private getServiceAmountValue(row: ConsolidatedScheduleRow): number | null {
    return this.toNumber(row.serviceDoctorAmount);
  }

  private getTotalToPayValue(row: ConsolidatedScheduleRow): number | null {
    return this.toNumber(row.totalDoctorToPay ?? row.amount);
  }

  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  openTransferModal(row: ConsolidatedScheduleRow): void {
    const consolidatedId = row.consolidatedScheduleId ?? row.indicationId?.toString() ?? null;
    const rowKey = this.getRowKey(row);
    if (rowKey) {
      this.actionLoadingId = rowKey;
      this.cd.markForCheck();
    }

    if (this.activeTab === 'inaptos') {
      this.openUnavailableTransferModal(row, consolidatedId);
      return;
    }

    const dialogRef = this.dialog.open(TransferConfirmationModalComponent, {
      width: '520px',
      data: {
        doctorName: row.doctorName || '-',
        specialtyName: row.specialtyName || '-',
        amount: this.getTotalToPayValue(row),
        indicationId: row.indicationId ?? null,
        consolidatedScheduleId: consolidatedId,
      },
      disableClose: true,
      hasBackdrop: false,
    });

    this.subs.add(
      dialogRef.afterClosed().subscribe((result) => {
        this.actionLoadingId = null;
        this.cd.markForCheck();
        if (result === 'success') {
          this.loginService.showMessage('Pagamento iniciado.');
          if (this.activeTab === 'aptos') {
            const removed = this.removeRowsFromTable(row);
            if (!removed) {
              this.applySearch();
            }
          } else {
            this.applySearch();
          }
        }
      })
    );
  }

  private openUnavailableTransferModal(
    row: ConsolidatedScheduleRow,
    consolidatedId: string | null
  ): void {
    if (!consolidatedId) {
      this.loginService.showMessage('ID da consolidação não encontrado para pagamento.');
      this.actionLoadingId = null;
      this.cd.markForCheck();
      return;
    }

    const dialogRef = this.dialog.open(InaptTransferModalComponent, {
      width: '600px',
      data: {
        doctorName: row.doctorName || '-',
        specialtyName: row.specialtyName || '-',
        amount: row.amount ?? null,
        indicationId: row.indicationId ?? null,
        consolidatedScheduleId: consolidatedId,
      },
      disableClose: true,
      hasBackdrop: false,
    });

    this.subs.add(
      dialogRef.afterClosed().subscribe((result) => {
        this.actionLoadingId = null;
        this.cd.markForCheck();
        if (result === 'success') {
          this.loginService.showMessage('Pagamento manual iniciado.', 4000);
          this.applySearch();
        }
      })
    );
  }

  private resolveSelectedHistory(
    historyId: string | null | undefined,
    selectedFromResponse: ConsolidatedScheduleHistory | null,
    histories: ConsolidatedScheduleHistory[]
  ): ConsolidatedScheduleHistory | null {
    if (historyId) {
      return selectedFromResponse ?? histories.find((h) => h.id === historyId) ?? null;
    }

    return selectedFromResponse ?? this.getLatestHistory(histories);
  }

  private getLatestHistory(
    histories: ConsolidatedScheduleHistory[]
  ): ConsolidatedScheduleHistory | null {
    if (!histories.length) return null;

    const sorted = this.sortHistories(histories);
    return sorted[0] ?? null;
  }

  private sortHistories(histories: ConsolidatedScheduleHistory[]): ConsolidatedScheduleHistory[] {
    return [...histories].sort((a, b) => {
      const dateA = this.getHistoryTimestamp(a);
      const dateB = this.getHistoryTimestamp(b);
      return dateB - dateA;
    });
  }

  private getHistoryTimestamp(history: ConsolidatedScheduleHistory): number {
    const fallbackDate = history.endDate ?? history.startDate;
    const value = history.consolidatedAt ?? fallbackDate;
    const parsed = Date.parse(String(value));
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.activeTab = this.tabs[event.index] ?? 'aptos';
    this.selectionEnabled = this.activeTab === 'aptos';
    this.selection.clear();
    this.useMaxPage = false;
    this.loading = true;
    this.resetTableState();
    if (this.activeTab !== 'aptos') {
      this.pageIndex = 0;
      this.pageSize = 10;
      this.pageSizeOptions = [10, 25, 50, 100, 250, 500];
    }

    this.applySearch();
  }

  onTransferAptasClick(): void {
    if (this.activeTab !== 'aptos') return;
    if (!this.selection.selected.length) {
      this.loginService.showMessage('Selecione ao menos um item para pagar.', 3000);
      return;
    }

    const totalAmount = this.getSelectedTotal();
    const dialogRef = this.dialog.open(TransferConfirmationModalComponent, {
      width: '520px',
      data: {
        doctorName: null,
        specialtyName: null,
        amount: null,
        indicationId: null,
        bulk: {
          totalAmount,
          totalCount: this.selection.selected.length,
          consolidatedScheduleIds: this.selection.selected
            .map((row) => row.consolidatedScheduleId)
            .filter((id) => !!id),
        },
        consolidatedScheduleId: null,
      },
      disableClose: true,
      hasBackdrop: true,
    });

    this.subs.add(
      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'success') {
          this.loginService.showMessage(
            `Pagamento iniciado para ${this.selection.selected.length} consolidação(ões).`,
            3000
          );
          const processedRows = [...this.selection.selected];
          this.selection.clear();
          if (this.activeTab === 'aptos') {
            const removed = this.removeRowsFromTable(processedRows);
            if (!removed) {
              this.applySearch();
            }
          } else {
            this.applySearch();
          }
        }
      })
    );
  }

  toggleAllRows(checked: boolean): void {
    if (!this.selectionEnabled || this.selectionWorking) return;

    this.startSelectionBusy(true);
    if (checked) {
      this.selectRowsInChunks(this.dataSource.data);
    } else {
      this.selection.clear();
      this.finishSelectionBusy();
    }
    this.cd.markForCheck();
  }

  toggleRowSelection(row: ConsolidatedScheduleRow, checked: boolean): void {
    if (!this.selectionEnabled || this.selectionWorking) return;
    this.selectionRowLoadingId = this.getRowKey(row);
    this.startSelectionBusy();
    requestAnimationFrame(() => {
      if (checked) {
        this.selection.select(row);
      } else {
        this.selection.deselect(row);
      }
      this.finishSelectionBusy();
      this.cd.markForCheck();
    });
    this.cd.markForCheck();
  }

  isAllSelected(): boolean {
    const dataLength = this.dataSource.data.length;
    return this.selectionEnabled && dataLength > 0 && this.selection.selected.length === dataLength;
  }

  isIndeterminate(): boolean {
    const selected = this.selection.selected.length;
    return this.selectionEnabled && selected > 0 && selected < this.dataSource.data.length;
  }

  get shouldShowReasonColumn(): boolean {
    return this.activeTab === 'inaptos';
  }

  get showSelectionColumn(): boolean {
    return this.activeTab === 'aptos' && this.selectionEnabled;
  }

  get selectedCount(): number {
    return this.selection.selected.length;
  }

  get isBlocking(): boolean {
    return this.loading;
  }

  get isTableBusy(): boolean {
    return this.loading || this.selectionWorking;
  }

  get isTopSpinnerBusy(): boolean {
    return this.loading || this.selectionWorking;
  }

  private renderRowsInChunks(
    rows: ConsolidatedScheduleRow[],
    options?: {
      append?: boolean;
      keepSelectionBusy?: boolean;
      preserveSelection?: boolean;
      suppressFinish?: boolean;
    }
  ): void {
    const {
      append = false,
      keepSelectionBusy = false,
      preserveSelection = false,
      suppressFinish = false,
    } = options || {};
    const chunkSize = 50;
    this.clearSelectionBusyTimer();

    if (!rows?.length) {
      requestAnimationFrame(() => {
        if (!append || !preserveSelection) {
          this.selection.clear();
        }
        this.dataSource.data = append ? this.dataSource.data : [];
        this.finishLoading();
      });
      return;
    }

    const bufferedRows: ConsolidatedScheduleRow[] = append ? [...this.dataSource.data] : [];
    let index = 0;

    const appendChunk = () => {
      const slice = rows.slice(index, index + chunkSize);
      if (slice.length) {
        bufferedRows.push(...slice);
        index += chunkSize;
      }

      if (index < rows.length) {
        setTimeout(appendChunk, 0);
      } else {
        requestAnimationFrame(() => {
          if (!append || !preserveSelection) {
            this.selection.clear();
          }
          if (!keepSelectionBusy) {
            this.selectionWorking = false;
            this.selectionAllLoading = false;
            this.selectionRowLoadingId = null;
          }
          this.actionLoadingId = null;
          this.dataSource.data = bufferedRows;
          if (!suppressFinish) {
            this.finishLoading();
          }
          this.cd.markForCheck();
        });
      }
    };

    appendChunk();
  }

  private resetTableState(): void {
    this.dataSource.data = [];
    this.selection.clear();
    this.selectionWorking = false;
    this.actionLoadingId = null;
    this.selectionRowLoadingId = null;
    this.selectionAllLoading = false;
    this.clearLoadingTimer();
    this.clearSelectionBusyTimer();
    this.updateDisplayedColumns();
  }

  private removeRowsFromTable(rows: ConsolidatedScheduleRow | ConsolidatedScheduleRow[]): boolean {
    const items = Array.isArray(rows) ? rows : [rows];
    if (!items.length) return false;

    const keys = new Set(
      items.map((row) => this.getRowKey(row)).filter((key): key is string => !!key)
    );

    if (!keys.size) return false;

    let removedCount = 0;
    const updated = this.dataSource.data.filter((item) => {
      const key = this.getRowKey(item);
      if (key && keys.has(key)) {
        removedCount += 1;
        return false;
      }
      return true;
    });

    if (!removedCount) return false;

    items.forEach((row) => this.selection.deselect(row));
    this.dataSource.data = updated;
    this.total = Math.max(0, this.total - removedCount);
    if (this.useMaxPage) {
      this.pageSize = updated.length;
    }
    this.cd.markForCheck();
    return true;
  }

  private selectRowsInChunks(rows: ConsolidatedScheduleRow[]): void {
    if (!rows?.length) return;

    const chunkSize = 200;
    let index = 0;
    this.startSelectionBusy(true);

    const renderChunk = () => {
      const slice = rows.slice(index, index + chunkSize);
      if (slice.length) {
        slice.forEach((row) => this.selection.select(row));
        index += chunkSize;
      }

      if (index < rows.length) {
        requestAnimationFrame(renderChunk);
      } else {
        this.finishSelectionBusy();
      }
    };

    requestAnimationFrame(renderChunk);
  }

  private loadAllAptosAndSelect(): void {
    if (!this.isAptosTab) return;
    this.startSelectionBusy(true);
    this.selectRowsInChunks(this.dataSource.data);
  }

  private startSelectionBusy(isSelectAll = false): void {
    if (this.selectionWorking) return;
    this.selectionAllLoading = isSelectAll;
    this.selectionWorking = true;
    this.selectionBusyStartedAt = performance.now();
    this.clearSelectionBusyTimer();
    this.cd.markForCheck();
  }

  private finishSelectionBusy(): void {
    const elapsed = performance.now() - this.selectionBusyStartedAt;
    const delay = Math.max(0, MIN_SELECTION_SPINNER - elapsed);
    this.clearSelectionBusyTimer();
    this.selectionBusyTimer = setTimeout(() => {
      this.selectionWorking = false;
      this.selectionAllLoading = false;
      this.selectionRowLoadingId = null;
      this.clearSelectionBusyTimer();
      this.cd.markForCheck();
    }, delay);
  }

  private clearSelectionBusyTimer(): void {
    if (this.selectionBusyTimer) {
      clearTimeout(this.selectionBusyTimer);
      this.selectionBusyTimer = null;
    }
  }

  private startLoading(): void {
    this.loadingStartedAt = performance.now();
    this.loading = true;
    this.clearLoadingTimer();
    this.cd.markForCheck();
  }

  private clearLoadingTimer(): void {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
  }

  trackByRow(index: number, row: ConsolidatedScheduleRow): string | number {
    return row.consolidatedScheduleId ?? row.indicationId ?? index;
  }

  getRowKey(row: ConsolidatedScheduleRow): string | null {
    return row.consolidatedScheduleId ?? (row.indicationId ? String(row.indicationId) : null);
  }

  private updateDisplayedColumns(): void {
    if (this.activeTab === 'pagos') {
      this.displayedColumns = [...this.paidColumns];
      return;
    }

    const columns = [...this.baseColumns];
    if (this.shouldShowReasonColumn) {
      const actionsIndex = columns.indexOf('actions');
      const insertIndex = actionsIndex === -1 ? columns.length : actionsIndex;
      columns.splice(insertIndex, 0, 'reason');
    }

    this.displayedColumns = this.showSelectionColumn ? ['select', ...columns] : columns;
  }

  private confirmMultipleTransfer(totalAmount: number): void {
    const ids = this.selection.selected
      .map((row) => row.consolidatedScheduleId)
      .filter((id) => !!id);

    if (!ids.length) {
      this.loginService.showMessage('Nenhuma consolidação selecionada.');
      return;
    }

    this.transferService.batchTransfer(ids).subscribe({
      next: () => {
        this.loginService.showMessage(
          `Pagamento iniciado para ${ids.length} consolidação(ões).`,
          3000
        );
        this.selection.clear();
        this.applySearch();
      },
      error: () => {
        this.loginService.showMessage('Falha ao iniciar múltiplos pagamentos.', 4000);
      },
    });
  }

  getSelectedTotal(): number {
    return this.selection.selected.reduce((acc, row) => {
      const amount = Number(this.getTotalToPayValue(row));
      return acc + (isNaN(amount) ? 0 : amount);
    }, 0);
  }

  extractDescription(data: string) {
    if (!data) {
      return '';
    }

    const firstParse = JSON.parse(data);
    const secondParse = JSON.parse(firstParse);
    return secondParse.errors[0].description;
  }

  private syncPageSizeOptions(total: number): void {
    const set = new Set(this.pageSizeOptions);
    if (total && total <= MAX_PAGE_SIZE && !set.has(total)) {
      set.add(total);
    }
    this.pageSizeOptions = Array.from(set)
      .filter((value) => value <= MAX_PAGE_SIZE)
      .sort((a, b) => a - b);
  }

  private finishLoading(): void {
    const elapsed = performance.now() - this.loadingStartedAt;
    const delay = Math.max(0, MIN_LOADING_SPINNER - elapsed);

    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
    }

    this.loadingTimer = setTimeout(() => {
      this.loading = false;
      this.loadingTimer = null;
      this.cd.markForCheck();
    }, delay);
  }

  private transferSingle(row: ConsolidatedScheduleRow): void {
    const consolidatedId = row.consolidatedScheduleId ?? row.indicationId?.toString();
    if (!consolidatedId) {
      this.loginService.showMessage('ID da consolidação não encontrado para pagamento.');
      return;
    }

    this.transferService.transferSingle(consolidatedId).subscribe({
      next: () => {
        this.loginService.showMessage('Pagamento iniciado.');
        this.applySearch();
      },
      error: () => {
        this.loginService.showMessage('Falha ao iniciar pagamento desta consolidação.', 4000);
      },
    });
  }
}
